import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useDerivedNetworkContext, useFilterContext } from '@/context';
import useIsBeforeDataAvailability, { type DataAvailabilityType } from './useIsBeforeDataAvailability';

type QueryHook<TData, TAdditionalParams> = (
  networkId: string,
  from: string,
  to: string,
  options?: Omit<UseQueryOptions<TData, Error>, 'queryKey' | 'queryFn'>,
  additionalParams?: TAdditionalParams
) => UseQueryResult<TData, Error>;

type AvailabilityQueryOptions = {
  /** Extra gate combined with the availability cutoff. Omitted leaves the query hook's own guard. */
  enabled?: boolean;
  /** Audience source filter. Uniques does not take sources. */
  includeSources?: boolean;
};

/**
 * Queries the page filter range, moving the start forward to the availability cutoff when the selection begins earlier.
 * Skips the request when the whole selected range ends before that cutoff.
 */
const useAvailabilityQuery = <
  TData,
  TAdditionalParams extends { showIds?: string[] } = { showIds?: string[] },
>(
  dataType: DataAvailabilityType,
  queryHook: QueryHook<TData, TAdditionalParams>,
  additionalParams?: Omit<TAdditionalParams, 'showIds' | 'sources'>,
  { enabled, includeSources = true }: AvailabilityQueryOptions = {}
) => {
  const { showBanner, hideContent, queryFrom } = useIsBeforeDataAvailability(dataType);
  const { selectedNetwork } = useDerivedNetworkContext();
  const { filters } = useFilterContext();
  const { audienceSources } = useFlags<{ audienceSources?: string[] }>();
  const sources = audienceSources ?? ['acast', 'phyllo'];
  const showIds = filters.selectedShows.length > 0 ? filters.selectedShows : undefined;
  const queryEnabled = hideContent ? false : enabled;

  const queryResult = queryHook(
    selectedNetwork.id ?? '',
    queryFrom,
    filters.dateRange.to,
    queryEnabled === undefined ? undefined : { enabled: queryEnabled },
    {
      showIds,
      ...additionalParams,
      ...(includeSources ? { sources } : {}),
    } as TAdditionalParams
  );

  return { showBanner, hideContent, queryResult };
};

export default useAvailabilityQuery;
